/**
 * Contract Template Editor
 *
 * Admin-only interface for editing contract template sections
 */

import React, { useState } from 'react';
import { LwModal } from '@/components/lw';
import { Edit, RotateCcw, Search, AlertCircle, ArrowUp, ArrowDown } from 'lucide-react';
import type { ContractTemplate, ContractSection, ContractVariableData } from './contract-types';
import { SectionEditorModal } from './SectionEditorModal';
import { createDefaultContractTemplate } from './contract-seed-template';

interface ContractTemplateEditorProps {
  template: ContractTemplate;
  variableData: ContractVariableData;
  onClose: () => void;
  onSave: (template: ContractTemplate) => void;
}

export const ContractTemplateEditor: React.FC<ContractTemplateEditorProps> = ({
  template,
  variableData,
  onClose,
  onSave
}) => {
  const [editingSection, setEditingSection] = useState<ContractSection | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [workingTemplate, setWorkingTemplate] = useState<ContractTemplate>(template);

  // Filter sections by search query
  const filteredSections = workingTemplate.sections.filter((section) =>
    section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    section.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort sections by order
  const sortedSections = [...filteredSections].sort((a, b) => {
    if (a.order === 'title') {
      return -1;
    }
    if (b.order === 'title') {
      return 1;
    }
    return a.order - b.order;
  });

  // Handle section update
  const handleSectionUpdate = (updatedSection: ContractSection) => {
    const updatedTemplate: ContractTemplate = {
      ...workingTemplate,
      sections: workingTemplate.sections.map((section) =>
        section.id === updatedSection.id ? updatedSection : section
      ),
      updatedAt: new Date().toISOString()
    };

    setWorkingTemplate(updatedTemplate);
    setEditingSection(null);
  };

  // Handle reset section to default
  const handleResetSection = (sectionId: string) => {
    // eslint-disable-next-line no-alert -- intentional native confirm
    if (!confirm('Reset this section to default? This will remove all customizations.')) {
      return;
    }

    // Find the section to reset
    const sectionToReset = workingTemplate.sections.find(s => s.id === sectionId);
    if (!sectionToReset) {
      console.error('Section not found:', sectionId);
      return;
    }

    // Get the default template and find the matching section by type
    const defaultTemplate = createDefaultContractTemplate();
    const defaultSection = defaultTemplate.sections.find(s => s.type === sectionToReset.type);

    if (!defaultSection) {
      console.error('Default section not found for type:', sectionToReset.type);
      return;
    }

    // Reset the section while preserving the ID and order
    const resetSection: ContractSection = {
      ...defaultSection,
      id: sectionToReset.id, // Preserve the current ID
      order: sectionToReset.order // Preserve the current order
    };

    // Update the working template
    const updatedTemplate: ContractTemplate = {
      ...workingTemplate,
      sections: workingTemplate.sections.map((section) =>
        section.id === sectionId ? resetSection : section
      ),
      updatedAt: new Date().toISOString()
    };

    setWorkingTemplate(updatedTemplate);
  };

  // Handle move section up or down
  const handleMoveSection = (sectionId: string, direction: 'up' | 'down') => {
    const section = workingTemplate.sections.find(s => s.id === sectionId);
    if (!section) {
      return;
    }

    const currentOrder = section.order;
    const targetOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;

    // Find section to swap with
    const targetSection = workingTemplate.sections.find(s => s.order === targetOrder);
    if (!targetSection) {
      return;
    } // Already at edge

    // Swap order values
    const updatedSections = workingTemplate.sections.map(s => {
      if (s.id === sectionId) {
        return { ...s, order: targetOrder };
      }
      if (s.id === targetSection.id) {
        return { ...s, order: currentOrder };
      }
      return s;
    });

    const updatedTemplate: ContractTemplate = {
      ...workingTemplate,
      sections: updatedSections,
      updatedAt: new Date().toISOString()
    };

    setWorkingTemplate(updatedTemplate);
  };

  // Handle save all changes
  const handleSave = () => {
    onSave(workingTemplate);
  };

  return (
    <>
      <LwModal
        size="tall"
        eyebrow="Contract"
        title="Edit Contract Template"
        sub="Customize contract sections. Changes will apply to all new candidates."
        onClose={onClose}
        footer={
          <>
            <span className="meta">Last updated: {new Date(workingTemplate.updatedAt).toLocaleString()}</span>
            <button type="button" className="btn-sm" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-sm danger"
              onClick={() => {
                // eslint-disable-next-line no-alert -- intentional native confirm
                if (confirm('Reset entire template to defaults? This will discard ALL customizations.')) {
                  localStorage.removeItem('lendwise_contract_template');
                  window.location.reload();
                }
              }}
            >
              Reset to Defaults
            </button>
            <button type="button" className="btn-sm solid" onClick={handleSave}>
              Save Changes
            </button>
          </>
        }
      >
        {/* Search and Stats */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div className="field" style={{ flex: 1 }}>
            <div className="field-affix has-l">
              <span className="affix l"><Search size={15} /></span>
              <input
                placeholder="Search sections..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <span className="badge" style={{ background: '#EEEADD', color: '#6F6A57' }}>
              {workingTemplate.sections.length} Sections
            </span>
            <span className="badge" style={{ background: '#EEEADD', color: '#6F6A57' }}>
              Version {workingTemplate.version}
            </span>
          </div>
        </div>

        {/* Section List */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            border: '1px solid var(--line)',
            borderRadius: 'var(--lw-radius)',
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 12
          }}
        >
          {sortedSections.map((section) => (
            <div key={section.id} className="panel" style={{ padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <h3 style={{ fontWeight: 600, color: 'var(--green-900)', fontSize: 14, margin: 0 }}>
                      {section.order === 'title' ? 'Title' : section.order}. {section.title}
                    </h3>
                    {section.hasDynamicData && (
                      <span className="badge" style={{ background: '#E2EFE6', color: '#246042', height: 21, fontSize: 11 }}>
                        Dynamic
                      </span>
                    )}
                    {!section.isEditable && (
                      <span className="badge" style={{ background: '#EEEADD', color: '#6F6A57', height: 21, fontSize: 11 }}>
                        Read-only
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--lw-muted)', margin: 0 }}>
                    {section.content.length} content items • {section.type}
                  </p>
                </div>

                <div style={{ display: 'flex', gap: 4 }}>
                  {/* Move Up Button */}
                  <button
                    type="button"
                    className="iconbtn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveSection(section.id, 'up');
                    }}
                    disabled={section.order === 1}
                    style={section.order === 1 ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                  >
                    <ArrowUp />
                  </button>

                  {/* Move Down Button */}
                  <button
                    type="button"
                    className="iconbtn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveSection(section.id, 'down');
                    }}
                    disabled={section.order === workingTemplate.sections.length}
                    style={section.order === workingTemplate.sections.length ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                  >
                    <ArrowDown />
                  </button>

                  {/* Edit Button */}
                  <button
                    type="button"
                    className="btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingSection(section);
                    }}
                  >
                    <Edit />
                    Edit
                  </button>

                  {/* Reset Button */}
                  {section.isEditable && (
                    <button
                      type="button"
                      className="iconbtn"
                      title="Reset section to default"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResetSection(section.id);
                      }}
                    >
                      <RotateCcw />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {sortedSections.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <AlertCircle size={48} style={{ color: 'var(--lw-muted)', margin: '0 auto 16px', display: 'block' }} />
              <p style={{ color: 'var(--lw-muted)', margin: 0 }}>No sections found matching "{searchQuery}"</p>
            </div>
          )}
        </div>
      </LwModal>

      {/* Section Editor Modal */}
      {editingSection && (
        <SectionEditorModal
          section={editingSection}
          variableData={variableData}
          onClose={() => setEditingSection(null)}
          onSave={handleSectionUpdate}
        />
      )}
    </>
  );
};
